import { OPEN_REVISION_STATES, type RevisionState } from '@felix/domain';
import { Button, CloseButton, SegmentedControl, Skeleton, TextInput } from '@mantine/core';
import {
  IconArchive,
  IconChevronRight,
  IconFileText,
  IconFolder,
  IconSearch,
} from '@tabler/icons-react';
import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Documents.module.css';
import { OTHER_GROUP_ID, otherDocumentIds, RETIRED_GROUP_ID } from './folder-tree.js';
import {
  buildTree,
  filterTree,
  folderLabel,
  isUpdated,
  type LibraryNode,
  recallTreeSort,
  rememberTreeSort,
  revisionShort,
  revisionTone,
  TREE_SORTS,
  type TreeSort,
  treeKeyAction,
  treeLabel,
  treeRows,
} from './library.js';
import { awaitingApproval, pendingTree } from './office.js';
import { RecordChildItem, type TreeRecords, useTreeRecordStates } from './TreeRecords.js';
import { type RecordChild, recordChildren } from './tree-records.js';
import type { LibraryDocument, LibrarySection, LibraryTree } from './types.js';
import { ownerBadge } from './vessel-library.js';

export interface DocumentTreeProps {
  section: LibrarySection;
  /** `null`: yükleniyor. */
  tree: LibraryTree | null;
  selectedId: string | null;
  /** "Updated" rozeti için şimdiki an (ISO). */
  now: string;
  onSelect: (documentId: string) => void;
  /** Arama temizlenince seçim değişmez; yalnız boş durumdan çıkış için. */
  onClearSelection: () => void;
  className?: string | undefined;
  /** Çekmecede başlık çekmecenindir: panel başlığı yinelenmez (inceleme M8a3-R38). */
  hideTitle?: boolean | undefined;
  /**
   * Web ofiste "Pending approval" süzgeci (plan M8a Task 17); verilince açık revizyonların durum
   * rozeti de görünür (Draft, Pending Approval …). Gemide yok.
   */
  filter?: 'all' | 'pending' | undefined;
  onFilter?: ((filter: 'all' | 'pending') => void) | undefined;
  /**
   * Birleşik kütüphane (gemide; ofiste bir geminin kütüphanesi; M8a4e): şirket belgesi "Company"
   * rozetli; gemi belgesinin açık revizyonu gemide durumuyla, ofiste "Draft (on vessel)".
   */
  unified?: boolean | undefined;
  /**
   * ISM ağacında formun kayıtları (ADR 0072): form düğümü açılır, altında geminin son kayıtları,
   * bekleyenler ve "All (n)". Verilmezse belgeler uç düğümdür.
   */
  records?: TreeRecords | undefined;
}

function allFolderIds(nodes: readonly LibraryNode[]): string[] {
  return nodes.flatMap((node) =>
    node.kind === 'folder' ? [node.id, ...allFolderIds(node.children)] : [],
  );
}

/**
 * Alt düzeyin kılavuz çizgisi (ADR 0116 §5): üst satırın açma okunun ortasından iner; satırın
 * girintisiyle aynı hesap (8 + (düzey − 1) × 18, ok 14 px).
 */
function guide(level: number): CSSProperties {
  return { '--tree-guide-x': `${8 + (level - 1) * 18 + 7}px` } as CSSProperties;
}

/** Klasörün (alt klasörleri dahil) doküman sayısı. */
function documentCount(nodes: readonly LibraryNode[]): number {
  return nodes.reduce(
    (sum, node) => sum + (node.kind === 'folder' ? documentCount(node.children) : 1),
    0,
  );
}

/**
 * Dokümanın ağaçtaki rozetleri (ADR 0028 #39): Updated, Withdrawn, Not on board; ofiste açık
 * revizyonun durumu (Draft, Pending Approval, Approved, Scheduled). Birleşik kütüphanede şirket
 * belgesi "Company"; gemi belgesinin açık revizyonu gemide durumuyla, ofiste "Draft (on vessel)"
 * (ADR 0038 §3, §12).
 */
function documentBadges(
  document: LibraryDocument,
  now: string,
  t: (key: string) => string,
  options: { office: boolean; unified: boolean; readOnly: boolean },
) {
  const badges: ReactNode[] = [];
  const latest = document.latest;
  // Yürürlükteki revizyon kısa haliyle ("Rev.3"; ortağın geri bildirimi 2026-10-17)
  if (document.current) {
    badges.push(
      <span key="rev" className={classes.treeRev}>
        {revisionShort(document.current.revisionNo)}
      </span>,
    );
  }
  const vessel = document.owner === 'vessel';
  if (ownerBadge(document, options.unified) === 'company') {
    badges.push(
      <StatusBadge key="owner" tone="neutral">
        {t('documents.library.company')}
      </StatusBadge>,
    );
  }
  if (options.readOnly && vessel && document.draftOnVessel) {
    badges.push(
      <StatusBadge key="draft" tone="warning">
        {t('documents.library.draftOnVessel')}
      </StatusBadge>,
    );
  } else if (
    (options.office || (options.unified && !options.readOnly && vessel)) &&
    latest &&
    latest.revisionId !== document.current?.revisionId &&
    OPEN_REVISION_STATES.includes(latest.state)
  ) {
    badges.push(
      <OpenRevisionBadge key="open" revisionNo={latest.revisionNo} state={latest.state} />,
    );
  }
  if (document.withdrawn) {
    badges.push(
      <StatusBadge key="withdrawn" tone="danger">
        {t('documents.badges.withdrawn')}
      </StatusBadge>,
    );
  } else if (document.current && document.onBoard === false) {
    badges.push(
      <StatusBadge key="board" tone="warning">
        {t('documents.badges.notOnBoard')}
      </StatusBadge>,
    );
  } else if (isUpdated(document, now)) {
    badges.push(
      <StatusBadge key="updated" tone="info">
        {t('documents.badges.updated')}
      </StatusBadge>,
    );
  }
  return badges;
}

/**
 * Açık (yürürlüğe girmemiş) revizyonun rozeti, numarasıyla (ortağın geri bildirimi 2026-10-17;
 * ADR 0069): onay bekleyende kısa "Rev.4 ⏳", tam metin ekran okuyucuda ve ipucunda ("Rev.4
 * awaiting approval"); taslakta "Rev.4 draft".
 */
function OpenRevisionBadge({ revisionNo, state }: { revisionNo: string; state: RevisionState }) {
  const { t } = useTranslation();
  const revision = revisionShort(revisionNo);
  const full = t(`documents.tree.openRevision.${state}`, { revision });
  return (
    <span title={full}>
      <StatusBadge tone={revisionTone(state)}>
        {state === 'pending_approval' ? (
          <>
            {revision} <span aria-hidden="true">⏳</span>
            <span className={classes.srOnly}>{t('documents.tree.awaitingApproval')}</span>
          </>
        ) : (
          full
        )}
      </StatusBadge>
    </span>
  );
}

/**
 * Form Tree / Manual Tree (gemi-ism-formlari.jpeg; plan M8a Task 14): arama kutusu, açılır
 * klasörler ve dokümanlar. `role="tree"`: ok tuşları, Home, End, Enter ve boşlukla gezilir; tek
 * sekme durağı (roving tabindex). Arama no, başlık ve açıklamada yapılır, eşleşen klasörleri
 * açar [sorular #37]. Klasörler başta açıktır; kapatılanlar hatırlanır.
 */
export function DocumentTree({
  section,
  tree,
  selectedId,
  now,
  onSelect,
  onClearSelection,
  className,
  filter,
  onFilter,
  hideTitle = false,
  unified = false,
  records,
}: DocumentTreeProps) {
  const { t } = useTranslation();
  const labels = (key: string) => t(`documents.sections.${section}.${key}`);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // Klasör içi sıra: "order" (varsayılan) ya da "date"; seçim bu cihazda hatırlanır (ADR 0069)
  const [sort, setSort] = useState<TreeSort>(() => recallTreeSort());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const items = useRef(new Map<string, HTMLDivElement>());
  const baseId = useId();
  // Açık form düğümleri (ADR 0072): başta kapalı; kayıtlar açılınca tembel okunur
  const [openForms, setOpenForms] = useState<ReadonlySet<string>>(
    () => new Set(records?.initial?.open ?? []),
  );
  const recordStates = useTreeRecordStates(records, openForms);
  const formChildren = (documentId: string): RecordChild[] =>
    recordChildren(documentId, recordStates.state(documentId));

  const office = filter !== undefined;
  const pending = useMemo(
    () => (tree ? tree.documents.filter(awaitingApproval).length : 0),
    [tree],
  );
  const shown = useMemo(
    () => (tree && filter === 'pending' ? pendingTree(tree) : tree),
    [tree, filter],
  );
  const nodes = useMemo(() => (shown ? buildTree(shown, sort) : []), [shown, sort]);
  const filtered = useMemo(() => filterTree(nodes, query), [nodes, query]);
  const searching = query.trim() !== '';
  const expanded = useMemo(() => {
    const folders = searching
      ? filtered.expand
      : new Set(allFolderIds(nodes).filter((id) => !collapsed.has(id)));
    return openForms.size > 0 ? new Set([...folders, ...openForms]) : folders;
  }, [searching, filtered, nodes, collapsed, openForms]);
  const withRecords = records !== undefined;
  const rows = treeRows(
    filtered.nodes,
    expanded,
    withRecords
      ? {
          expandable: (document) => document.kind === 'form',
          rows: (documentId) => formChildren(documentId).map((child) => child.id),
        }
      : undefined,
  );
  const rowIds = new Set(rows.map((row) => row.id));
  // Sekme durağı: odaktaki satır, yoksa seçili doküman, o da görünmüyorsa ilk satır
  const tabStop =
    (focusedId && rowIds.has(focusedId) && focusedId) ||
    (selectedId && rowIds.has(selectedId) && selectedId) ||
    rows[0]?.id ||
    null;

  // Seçilen dokümanın klasörleri kapalıysa açılır (adresten gelen seçim görünür olsun)
  useEffect(() => {
    if (!selectedId || !tree) return;
    const document = tree.documents.find((d) => d.id === selectedId);
    if (!document) return;
    const parents = new Map(tree.folders.map((f) => [f.id, f.parentId]));
    const retired = new Set(tree.folders.filter((f) => f.retired).map((f) => f.id));
    // Belge bir gösterim grubundaysa grup da açılır (ADR 0038 §3, §9)
    const chain: string[] = [];
    for (let id: string | null | undefined = document.folderId; id; id = parents.get(id)) {
      chain.push(id);
    }
    if (otherDocumentIds(tree).has(document.id)) chain.push(OTHER_GROUP_ID);
    else if (chain.some((id) => retired.has(id))) chain.push(RETIRED_GROUP_ID);
    setCollapsed((current) => {
      let next: Set<string> | null = null;
      for (const id of chain) {
        if (current.has(id)) {
          next ??= new Set(current);
          next.delete(id);
        }
      }
      return next ?? current;
    });
  }, [selectedId, tree]);

  const toggleForm = (id: string) =>
    setOpenForms((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggle = (id: string) => {
    if (rows.find((row) => row.id === id)?.kind === 'document') {
      toggleForm(id);
      return;
    }
    if (searching) return;
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const focus = (id: string) => {
    setFocusedId(id);
    items.current.get(id)?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = tabStop;
    if (!current) return;
    const action = treeKeyAction(rows, focusedId ?? current, event.key, expanded, { searching });
    if (!action) return;
    event.preventDefault();
    if ('focus' in action) focus(action.focus);
    else if ('toggle' in action) toggle(action.toggle);
    else if ('activate' in action) activate(action.activate);
    else onSelect(action.select);
  };

  /** Kayıt satırı: kaydı ya da formun kayıt listesini açar; hata satırı yeniden dener. */
  function activate(id: string) {
    const parent = rows.find((row) => row.id === id)?.parentId;
    const child = parent ? formChildren(parent).find((c) => c.id === id) : undefined;
    if (!records || !parent || !child) return;
    if (child.kind === 'record') records.openRecord(child.item.id);
    else if (child.kind === 'all') records.openAll(parent);
    else if (child.kind === 'error') recordStates.retry();
  }

  const renderRecords = (documentId: string, level: number): ReactNode => {
    const state = recordStates.state(documentId);
    const roles = state?.kind === 'ready' ? state.view.roles : {};
    return formChildren(documentId).map((child) => (
      <RecordChildItem
        key={child.id}
        child={child}
        level={level}
        labelId={`${baseId}-${child.id}`}
        tabIndex={child.id === tabStop ? 0 : -1}
        roles={roles}
        itemRef={(element) => {
          if (element) items.current.set(child.id, element);
          else items.current.delete(child.id);
        }}
        onFocus={() => setFocusedId(child.id)}
        onActivate={() => activate(child.id)}
      />
    ));
  };

  const render = (list: readonly LibraryNode[], level: number): ReactNode =>
    list.map((node) => {
      const labelId = `${baseId}-${node.id}`;
      const tabIndex = node.id === tabStop ? 0 : -1;
      if (node.kind === 'folder') {
        const open = expanded.has(node.id);
        // Boş klasör (şablon başlığı; ADR 0037 §6) görünür ama açılıp kapanmaz
        const empty = node.children.length === 0;
        // Gösterim grupları: "Retired folders", "Other company documents" (ADR 0038 §3, §9)
        const group = node.folder.group;
        const text = group
          ? t(group === 'retired' ? 'documents.tree.retiredGroup' : 'documents.tree.otherGroup')
          : folderLabel(node.folder);
        const FolderIcon = group === 'retired' ? IconArchive : IconFolder;
        return (
          <div
            key={node.id}
            role="treeitem"
            aria-level={level}
            aria-expanded={empty ? undefined : open}
            aria-labelledby={labelId}
            tabIndex={tabIndex}
            className={classes.treeItem}
            ref={(element) => {
              if (element) items.current.set(node.id, element);
              else items.current.delete(node.id);
            }}
            onFocus={(event) => {
              if (event.target === event.currentTarget) setFocusedId(node.id);
            }}
          >
            {/* Satıra tıklama klasörü açar/kapatır; klavye ağacın kendisinde (onKeyDown) */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: klavye ağaç düzeyinde */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: treeitem'in görsel satırı */}
            <div
              className={classes.treeRow}
              data-kind="folder"
              data-group={group}
              data-retired={node.folder.retired || undefined}
              style={{ paddingInlineStart: 8 + (level - 1) * 18 }}
              onClick={() => {
                setFocusedId(node.id);
                toggle(node.id);
              }}
            >
              <span className={classes.treeChevron} data-open={open || undefined} aria-hidden>
                {empty ? (
                  <span className={classes.treeChevronSpace} />
                ) : (
                  <IconChevronRight size={14} stroke={2} className={classes.treeChevronIcon} />
                )}
              </span>
              <FolderIcon size={17} stroke={1.7} className={classes.treeFolderIcon} aria-hidden />
              <span id={labelId} className={classes.treeLabel} title={text}>
                {text}
              </span>
              {/* Klasördeki doküman sayısı (görsel; ad ekran okuyucuda değişmez) */}
              {empty ? null : (
                <span className={classes.treeCount} aria-hidden>
                  {documentCount(node.children)}
                </span>
              )}
            </div>
            {open && node.children.length > 0 ? (
              // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA ağacında alt düzey `group`'tur
              <div role="group" className={classes.treeGroup} style={guide(level)}>
                {render(node.children, level + 1)}
              </div>
            ) : null}
          </div>
        );
      }
      const document = node.document;
      const selected = node.id === selectedId;
      // Form düğümü açılır: altında kayıtlar (ADR 0072)
      const expandable = withRecords && document.kind === 'form';
      const open = expandable && openForms.has(node.id);
      const text = treeLabel(document);
      const badges = documentBadges(document, now, t, {
        office,
        unified,
        readOnly: tree?.readOnly === true,
      });
      return (
        <div
          key={node.id}
          role="treeitem"
          aria-level={level}
          aria-selected={selected}
          aria-expanded={expandable ? open : undefined}
          aria-labelledby={badges.length > 0 ? `${labelId} ${labelId}-badges` : labelId}
          tabIndex={tabIndex}
          className={classes.treeItem}
          ref={(element) => {
            if (element) items.current.set(node.id, element);
            else items.current.delete(node.id);
          }}
          onFocus={(event) => {
            if (event.target === event.currentTarget) setFocusedId(node.id);
          }}
        >
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: klavye ağaç düzeyinde */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: treeitem'in görsel satırı */}
          <div
            className={classes.treeRow}
            data-kind="document"
            data-selected={selected || undefined}
            data-expandable={expandable || undefined}
            style={{ paddingInlineStart: 8 + (level - 1) * 18 + (expandable ? 0 : 6) }}
            onClick={() => {
              setFocusedId(node.id);
              onSelect(node.id);
            }}
          >
            {expandable ? (
              // Ok formu açar/kapatır, satır formu seçer (klavyede sağ/sol ok ve Enter)
              <span
                className={classes.treeChevron}
                data-open={open || undefined}
                data-toggle
                title={t('documents.tree.records.expand')}
                aria-hidden
                onClick={(event) => {
                  event.stopPropagation();
                  setFocusedId(node.id);
                  toggleForm(node.id);
                }}
              >
                <IconChevronRight size={14} stroke={2} className={classes.treeChevronIcon} />
              </span>
            ) : null}
            <IconFileText size={16} stroke={1.6} className={classes.treeDocIcon} aria-hidden />
            <span id={labelId} className={classes.treeLabel} title={text}>
              {text}
            </span>
            {badges.length > 0 ? (
              <span id={`${labelId}-badges`} className={classes.treeBadges}>
                {badges}
              </span>
            ) : null}
          </div>
          {open ? (
            // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA ağacında alt düzey `group`'tur
            <div role="group" className={classes.treeGroup} style={guide(level)}>
              {renderRecords(node.id, level + 1)}
            </div>
          ) : null}
        </div>
      );
    });

  let body: ReactNode;
  if (!tree) {
    body = (
      <div className={classes.treeSkeleton} aria-busy="true">
        <span className={classes.srOnly}>{t('documents.screen.loading')}</span>
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Skeleton key={i} height={18} width={i % 3 === 0 ? '78%' : '64%'} ml={i % 3 ? 26 : 0} />
        ))}
      </div>
    );
  } else if (tree.documents.length === 0 && tree.folders.length === 0) {
    body = <EmptyState title={t('documents.tree.empty')} />;
  } else if (filter === 'pending' && pending === 0) {
    body = <EmptyState title={t('documents.filter.pendingEmpty')} />;
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        title={t('documents.tree.noMatch', { query: query.trim() })}
        action={
          <Button
            size="xs"
            variant="light"
            onClick={() => {
              setQuery('');
              onClearSelection();
            }}
          >
            {t('documents.tree.clear')}
          </Button>
        }
      />
    );
  } else {
    body = (
      <div role="tree" aria-label={labels('tree')} className={classes.tree} onKeyDown={onKeyDown}>
        {render(filtered.nodes, 1)}
      </div>
    );
  }

  return (
    <Panel
      {...(hideTitle ? {} : { title: labels('tree') })}
      aria-label={labels('tree')}
      className={[classes.treePanel, className].filter(Boolean).join(' ')}
    >
      {records?.picker}
      <TextInput
        className={classes.treeSearch}
        size="sm"
        leftSection={<IconSearch size={16} aria-hidden />}
        rightSection={
          query ? (
            <CloseButton
              size="sm"
              aria-label={t('documents.tree.clear')}
              onClick={() => setQuery('')}
            />
          ) : null
        }
        placeholder={labels('search')}
        aria-label={labels('search')}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
      />
      {onFilter ? (
        <SegmentedControl
          size="xs"
          fullWidth
          className={classes.treeFilter}
          value={filter ?? 'all'}
          onChange={(value) => onFilter(value === 'pending' ? 'pending' : 'all')}
          aria-label={t('documents.filter.label')}
          data={[
            { value: 'all', label: t('documents.filter.all') },
            { value: 'pending', label: t('documents.filter.pending', { count: pending }) },
          ]}
        />
      ) : null}
      <SegmentedControl
        size="xs"
        fullWidth
        className={classes.treeFilter}
        value={sort}
        onChange={(value) => {
          const next: TreeSort = value === 'date' ? 'date' : 'order';
          setSort(next);
          rememberTreeSort(next);
        }}
        aria-label={t('documents.tree.sort.label')}
        data={TREE_SORTS.map((value) => ({ value, label: t(`documents.tree.sort.${value}`) }))}
      />
      {/* Ağaç bu kutuda kayar */}
      <div className={classes.treeScroll}>{body}</div>
      {/* Ekran okuyucu: ağaçta kaç doküman görünüyor */}
      <span className={classes.srOnly} aria-live="polite">
        {tree && searching
          ? t('documents.tree.count', { count: rows.filter((r) => r.kind === 'document').length })
          : ''}
      </span>
    </Panel>
  );
}
